import React, { useEffect, useState } from 'react';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import api from '../../services/api';
import {
  ConfirmDialog,
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
  SectionCard,
} from '../../components/ui';

function ProjectListPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await api.get('/projects');
      setProjects(res.data || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleDeleteRequest = (project) => {
    setProjectToDelete(project);
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;

    setDeleting(true);
    try {
      await api.delete(`/projects/${projectToDelete.id}`);
      await fetchProjects();
    } catch (err) {
      console.error('Failed to delete project:', err);
      alert('Error deleting project.');
    } finally {
      setDeleting(false);
      setProjectToDelete(null);
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/projects/edit/${id}`);
  };

  const handleAddNew = () => {
    navigate('/admin/projects/new');
  };

  return (
    <PageLayout backgroundAccent>
      <PageHeader
        overline="Operations"
        title="Projects"
        subtitle="Manage project geofencing settings and edit operational metadata."
        actions={
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleAddNew}>
            Add New Project
          </Button>
        }
      />

      <SectionCard title={`Project List (${projects.length})`}>
        {loading ? (
          <LoadingState rows={8} header={false} />
        ) : projects.length === 0 ? (
          <EmptyState
            compact
            title="No projects found"
            description="Create your first project to start assigning attendance geofences."
            actionLabel="Add New Project"
            onAction={handleAddNew}
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Latitude</TableCell>
                  <TableCell>Longitude</TableCell>
                  <TableCell>Radius (m)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((proj) => (
                  <TableRow key={proj.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{proj.id}</TableCell>
                    <TableCell>{proj.name}</TableCell>
                    <TableCell>{proj.latitude ?? '-'}</TableCell>
                    <TableCell>{proj.longitude ?? '-'}</TableCell>
                    <TableCell>{proj.allowed_radius_in_meters}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => handleEdit(proj.id)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDeleteRequest(proj)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(projectToDelete)}
        title="Delete Project"
        description={
          projectToDelete
            ? `Are you sure you want to delete "${projectToDelete.name}"? This action cannot be undone.`
            : 'Are you sure you want to delete this project?'
        }
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onClose={() => setProjectToDelete(null)}
        onConfirm={handleDelete}
      />
    </PageLayout>
  );
}

export default ProjectListPage;

